import type { Arc, Marker } from "cobe";

type Point3 = readonly [number, number, number];

export const GLOBE_TILT = 0.24;
export const MARKER_ELEVATION = 0.015;
export const ARC_HEIGHT = 0.22;
const GLOBE_RADIUS = 0.8;

// Illustrative commercial hubs, not a complete Amazon marketplace map or account activity.
export const ILLUSTRATIVE_MARKERS: Marker[] = [
  { location: [40.71, -74.01], size: 0.012 },
  { location: [37.77, -122.42], size: 0.01 },
  { location: [51.51, -0.13], size: 0.012 },
  { location: [50.11, 8.68], size: 0.01 },
  { location: [35.68, 139.69], size: 0.012 },
  { location: [1.35, 103.82], size: 0.01 },
  { location: [-33.87, 151.21], size: 0.01 },
];

export const MARKETPLACE_ROUTES: Arc[] = [
  { from: [40.71, -74.01], to: [51.51, -0.13] },
  { from: [37.77, -122.42], to: [35.68, 139.69] },
  { from: [51.51, -0.13], to: [1.35, 103.82] },
  { from: [1.35, 103.82], to: [-33.87, 151.21] },
  { from: [35.68, 139.69], to: [50.11, 8.68] },
];

function locationToPoint([latitude, longitude]: [number, number]): Point3 {
  const lat = (latitude * Math.PI) / 180;
  const lon = (longitude * Math.PI) / 180 - Math.PI;
  return [-Math.cos(lat) * Math.cos(lon), Math.sin(lat), Math.cos(lat) * Math.sin(lon)];
}

const RIPPLE_FRAMES = MARKETPLACE_ROUTES.map(({ to: location }) => {
  const normal = locationToPoint(location);
  const longitude = (location[1] * Math.PI) / 180 - Math.PI;
  const tangent: Point3 = [Math.sin(longitude), 0, Math.cos(longitude)];
  const bitangent: Point3 = [
    normal[1] * tangent[2],
    normal[2] * tangent[0] - normal[0] * tangent[2],
    -normal[1] * tangent[0],
  ];
  return { normal, tangent, bitangent };
});

// One route clock drives both the moving light and its destination's response.
// The final 1.44 seconds of each cycle let the arrival ripples dissipate.
export function getRoutePhase(elapsed: number, routeIndex: number) {
  const head = (elapsed / 4800 + routeIndex * 0.23) % 1.3;
  return { head, arrivalAge: head >= 1 ? (head - 1) * 4800 : null };
}

function drawMarkerRipples(
  context: CanvasRenderingContext2D,
  width: number,
  phi: number,
  elapsed: number,
) {
  context.lineWidth = 1;
  RIPPLE_FRAMES.forEach(({ normal, tangent, bitangent }, routeIndex) => {
    const { arrivalAge } = getRoutePhase(elapsed, routeIndex);
    if (arrivalAge === null) return;
    for (let ring = 0; ring < 2; ring++) {
      const progress = (arrivalAge - ring * 160) / 950;
      if (progress < 0 || progress >= 1) continue;
      const angle = 0.007 + progress * 0.045;
      const opacity = 0.65 * Math.sin(progress * Math.PI) * (1 - progress);
      context.strokeStyle = `rgba(218, 245, 255, ${opacity})`;
      context.beginPath();
      let connected = false;
      // Project rings on the sphere so they tilt with the surface and disappear behind it.
      for (let step = 0; step <= 40; step++) {
        const phase = (step / 40) * Math.PI * 2;
        const point = normal.map(
          (value, axis) =>
            (GLOBE_RADIUS + MARKER_ELEVATION) *
            (value * Math.cos(angle) +
              Math.sin(angle) *
                (tangent[axis]! * Math.cos(phase) + bitangent[axis]! * Math.sin(phase))),
        ) as [number, number, number];
        const projected = projectGlobePoint(point, phi);
        if (!projected.visible) {
          connected = false;
          continue;
        }
        if (connected) context.lineTo(projected.x * width, projected.y * width);
        else context.moveTo(projected.x * width, projected.y * width);
        connected = true;
      }
      context.stroke();
    }
  });
}

// Match COBE 2's quadratic arc and orthographic projection so the light trails
// stay attached to its WebGL routes, including globe occlusion. See shuding/cobe/src/arc.glslx.
const ROUTE_POINTS = MARKETPLACE_ROUTES.map((route) => {
  const from = locationToPoint(route.from);
  const to = locationToPoint(route.to);
  const sum: Point3 = [from[0] + to[0], from[1] + to[1], from[2] + to[2]];
  const length = Math.hypot(...sum);
  const mid = sum.map((value) => (value / length) * (GLOBE_RADIUS + ARC_HEIGHT + MARKER_ELEVATION));
  return Array.from({ length: 129 }, (_, index): Point3 => {
    const t = index / 128;
    const u = 1 - t;
    const r = GLOBE_RADIUS + MARKER_ELEVATION;
    return [
      u * u * from[0] * r + 2 * u * t * mid[0]! + t * t * to[0] * r,
      u * u * from[1] * r + 2 * u * t * mid[1]! + t * t * to[1] * r,
      u * u * from[2] * r + 2 * u * t * mid[2]! + t * t * to[2] * r,
    ];
  });
});

export function projectGlobePoint(point: Point3, phi: number) {
  const [x, y, z] = point;
  const cy = Math.cos(phi);
  const sy = Math.sin(phi);
  const cx = Math.cos(GLOBE_TILT);
  const sx = Math.sin(GLOBE_TILT);
  const rx = cy * x + sy * z;
  const ry = sy * sx * x + cx * y - cy * sx * z;
  const depth = -sy * cx * x + sx * y + cy * cx * z;
  return {
    x: (rx + 1) / 2,
    y: (1 - ry) / 2,
    visible: depth >= 0 || rx * rx + ry * ry >= GLOBE_RADIUS ** 2,
  };
}

export function drawGlobeFlows(
  context: CanvasRenderingContext2D,
  width: number,
  phi: number,
  elapsed: number,
) {
  context.clearRect(0, 0, width, width);
  context.lineCap = "round";
  context.lineWidth = 1.4;
  ROUTE_POINTS.forEach((points, routeIndex) => {
    // Keep the head bright through arrival; the trailing light dissipates behind it.
    const { head } = getRoutePhase(elapsed, routeIndex);
    for (let index = 1; index < points.length; index++) {
      const progress = index / 128;
      const distance = head - progress;
      if (distance < 0 || distance > 0.14) continue;
      const previous = projectGlobePoint(points[index - 1]!, phi);
      const current = projectGlobePoint(points[index]!, phi);
      if (!previous.visible || !current.visible) continue;
      const opacity = (1 - distance / 0.14) * Math.min(1, progress * 12);
      context.strokeStyle = `rgba(244, 252, 255, ${opacity})`;
      context.beginPath();
      context.moveTo(previous.x * width, previous.y * width);
      context.lineTo(current.x * width, current.y * width);
      context.stroke();
    }
  });
  drawMarkerRipples(context, width, phi, elapsed);
}
